import type { DogAgeRange } from "~/data/dogAgeRanges"
import type { AskDynamoThoughtFrequency } from "~/data/askDynamoThoughtFrequencies"

const API_BASE = "/api/v1"

export interface DogPortraitInfo {
  status: "none" | "generating" | "ready" | "failed"
  url: string | null
  can_regenerate: boolean
  regeneration_blocked_reason: string | null
  validation_error: string | null
  failure_message: string | null
  generation_count: number
  complimentary_update_used: boolean
}

export interface ProfileResponse {
  user: {
    id: number
    email: string
    email_verified: boolean
    email_verified_at: string | null
    /** ISO8601; present when email is not verified (matches verification link validity window). */
    email_verification_link_expires_at: string | null
    premium: boolean
  }
  dog_profile: {
    name: string
    sex: "male" | "female"
    age_range: DogAgeRange | null
    archetype_key: string
    archetype_label: string | null
    archetype_icon: string | null
    /** Present when linked at signup; used for full results / CharacterCard */
    assessment_session_token?: string | null
    share_celebration_seen_at?: string | null
    portrait: DogPortraitInfo
  } | null
  kibble_frequency: "weekly" | "monthly" | "none" | null
  kibble_enabled: boolean
  next_delivery_at: string | null
  ask_dynamo_thought_preference: {
    frequency: AskDynamoThoughtFrequency
    enabled: boolean
    onboarding_completed_at: string | null
  } | null
  ask_dynamo?: {
    daily_limit: number
    daily_remaining: number
  }
}

const jsonHeaders = {
  "Content-Type": "application/json",
  Accept: "application/json",
}

async function apiErrorMessage(res: Response): Promise<string> {
  const err = await res.json().catch(() => null) as { errors?: string[] } | null
  if (err?.errors?.length) return err.errors.join(", ")
  if (res.status === 404 || res.status >= 500) {
    return "Something went wrong. If you just signed up, try logging in — your account may already exist."
  }
  return res.statusText || "Request failed"
}

export async function signup(
  email: string,
  password: string,
  assessmentSessionToken?: string | null,
  portraitPhoto?: File | null,
): Promise<ProfileResponse> {
  const form = new FormData()
  form.append("email", email)
  form.append("password", password)
  if (portraitPhoto) form.append("portrait_source_photo", portraitPhoto)
  if (assessmentSessionToken) form.append("assessment_session_token", assessmentSessionToken)

  const res = await fetch(`${API_BASE}/auth/signup`, {
    method: "POST",
    body: form,
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function login(email: string, password: string): Promise<ProfileResponse> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ email, password }),
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, { method: "DELETE", credentials: "include" })
}

export async function resendVerification(): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/resend_verification`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: "{}",
  })
  if (res.status === 204) return
  throw new Error(await apiErrorMessage(res))
}

export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/forgot_password`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ email }),
    credentials: "include",
  })
  if (res.status === 204) return
  throw new Error(await apiErrorMessage(res))
}

export async function resetPassword(token: string, password: string): Promise<ProfileResponse> {
  const res = await fetch(`${API_BASE}/auth/reset_password`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ token, password }),
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}

export async function getProfile(): Promise<ProfileResponse> {
  const res = await fetch(`${API_BASE}/profile`, { credentials: "include" })
  if (!res.ok) throw new Error("Not authenticated")
  return res.json()
}

export async function updateProfile(payload: {
  kibble_frequency?: "weekly" | "monthly" | "none"
  ask_dynamo_thought_frequency?: AskDynamoThoughtFrequency
  ask_dynamo_thought_onboarding_completed?: boolean
  dog_name?: string
  sex?: "male" | "female"
}): Promise<ProfileResponse> {
  const res = await fetch(`${API_BASE}/profile`, {
    method: "PATCH",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
    credentials: "include",
  })
  if (!res.ok) {
    throw new Error(await apiErrorMessage(res))
  }
  return res.json()
}
